import React, { FC, useCallback, useEffect, useRef } from 'react';
import { AllieLink } from '../../Buttons/Link';
import { AllieUploadOutlineIcon } from '../../../styling/icons';
import { StyledInput } from './FileInput.styles';
import { AllieProgressBar } from '../../ProgressIndicators/ProgressBar';
import { AllieCommonProps } from '../../../common/components.types';
import { fileTypeAcceptable } from './helpers';

type ErrorUpload = {
  code: number;
  message: string;
};

interface AllieFileInputProps extends AllieCommonProps {
  id: string;
  accept: string[];
  maxFileSize: number;
  onUploadProgress?: (progress: number) => void;
  onUploadError?: (error: ErrorUpload) => void;
  onUploadSuccess?: (response: any) => void;
  render?: (onClick: () => void) => JSX.Element;
  labelButton?: string;
  onChange?: (file: File) => void;
  progress?: number;
  showProgressBar?: boolean;
  useNativeEvent?: boolean;
}

export const AllieFileInput: FC<AllieFileInputProps> = ({
  id,
  accept,
  maxFileSize,
  onUploadError,
  onUploadProgress,
  onUploadSuccess,
  render,
  labelButton,
  onChange,
  progress,
  showProgressBar,
  useNativeEvent = false,
  ...props
}) => {
  const inputElement = useRef<HTMLInputElement>(null);

  const openFile = useCallback((e?: React.MouseEvent) => {
    e?.preventDefault();
    inputElement.current?.click();
  }, []);

  const processFile = useCallback(
    (file: File) => {
      if (file.size > maxFileSize) {
        onUploadError?.({ code: 1, message: `${file.name} is too large` });
        return;
      }

      if (!fileTypeAcceptable(accept, file)) {
        onUploadError?.({ code: 2, message: `${file.name} is not a valid file type` });
        return;
      }

      onChange?.(file);
    },
    [accept, maxFileSize, onChange, onUploadError]
  );

  const handleFile = useCallback(
    (e: React.ChangeEvent<HTMLInputElement> | Event) => {
      e.preventDefault();

      let file: File | undefined;

      if ('target' in e && e.target instanceof HTMLInputElement) {
        file = e.target.files?.[0];
      } else if (e instanceof Event && e.target instanceof HTMLInputElement) {
        file = e.target.files?.[0];
      }

      if (file) {
        processFile(file);
      }
    },
    [processFile]
  );

  useEffect(() => {
    const currentInput = inputElement.current;
    if (currentInput && useNativeEvent) {
      currentInput.addEventListener('change', handleFile);
      return () => {
        currentInput.removeEventListener('change', handleFile);
      };
    }
  }, [useNativeEvent, handleFile]);

  return (
    <div {...props}>
      <input
        ref={inputElement}
        type="file"
        accept={accept.join()}
        style={{ display: 'none' }}
        onChange={useNativeEvent ? undefined : handleFile}
        multiple={false}
        onClick={(e) => {
          (e.target as HTMLInputElement).value = '';
        }}
      />
      {render ? (
        render(openFile)
      ) : (
        <div style={{ width: 'fit-content' }}>
          <StyledInput>
            <AllieUploadOutlineIcon />
            <AllieLink onClick={openFile}>{labelButton}</AllieLink>
          </StyledInput>
          {showProgressBar && <AllieProgressBar value={progress} />}
        </div>
      )}
    </div>
  );
};
